'use client';
import React, { useState, memo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Flag, Car, Settings, MapPin } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';

const BrandPanel = memo(function BrandPanel() {
  return (
    <div className="hidden lg:flex flex-col w-1/2 relative overflow-hidden hero-gradient">
      <div className="absolute inset-0 checkered-bg opacity-20 pointer-events-none" />
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-primary via-accent to-primary" />
      <div className="relative z-10 flex flex-col h-full px-12 py-10">
        <div className="flex items-center gap-4">
          <AppLogo size={280} />
        </div>
        <div className="flex-1 flex flex-col justify-center">
          <h1 className="text-5xl xl:text-6xl font-extrabold uppercase leading-none tracking-tight mb-4">
            <span className="text-foreground">BACK</span>
            <br />
            <span className="text-gradient-red">ON THE</span>
            <br />
            <span className="text-foreground">GRID.</span>
          </h1>
          <p className="text-muted-foreground text-base max-w-sm leading-relaxed">
            Sign in to manage your race registrations, view your results, and stay connected with the SpeedPass community.
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <Flag size={14} className="text-primary" />
          <span>2026 Season — 3 Rounds Remaining</span>
        </div>
      </div>
    </div>
  );
});

export default function LoginPage() {
  const { signIn } = useAuth();
  const router = useRouter();
  const [mode, setMode] = useState<'choose' | 'driver' | 'organizer' | 'track-manager'>('choose');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsSubmitting(true);
    try {
      await signIn(email, password);
      toast.success('Welcome back to SpeedPass!');
      // Redirect track managers to their dashboard
      if (mode === 'track-manager') {
        router.push('/track-manager');
      } else {
        router.push('/');
      }
      router.refresh();
    } catch (err: any) {
      setAuthError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors';

  // Choose mode screen
  if (mode === 'choose') {
    return (
      <div className="min-h-screen flex bg-background">
        <BrandPanel />
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <div className="w-full max-w-sm">
            <div className="flex items-center gap-3 mb-8 lg:hidden">
              <AppLogo size={120} />
            </div>
            <div className="mb-8">
              <h2 className="text-2xl font-extrabold uppercase tracking-tight text-foreground mb-1">Sign In</h2>
              <p className="text-sm text-muted-foreground">Choose how you want to sign in</p>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => setMode('driver')}
                className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-border hover:border-primary/50 bg-card hover:bg-primary/5 transition-all group">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Car size={22} className="text-primary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-extrabold uppercase tracking-wide text-foreground">Driver / User</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Race participants & spectators</p>
                </div>
              </button>
              <button
                onClick={() => setMode('organizer')}
                className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-border hover:border-primary/50 bg-card hover:bg-primary/5 transition-all group">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Settings size={22} className="text-primary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-extrabold uppercase tracking-wide text-foreground">Organizer</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Event organizers & managers</p>
                </div>
              </button>
              <button
                onClick={() => setMode('track-manager')}
                className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-border hover:border-primary/50 bg-card hover:bg-primary/5 transition-all group">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <MapPin size={22} className="text-primary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-extrabold uppercase tracking-wide text-foreground">Track Manager</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Karting track owners & managers</p>
                </div>
              </button>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-6">
              New driver?{' '}
              <Link href="/register" className="text-primary font-semibold hover:text-accent transition-colors">
                Register here
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const modeConfig = {
    driver: { icon: <Car size={18} className="text-primary" />, title: 'Driver Sign In', registerLink: '/register', registerText: "Don\'t have an account?", registerLabel: 'Register here', submitLabel: 'Sign In' },
    organizer: { icon: <Settings size={18} className="text-primary" />, title: 'Organizer Sign In', registerLink: '/organizer-register', registerText: 'New organizer?', registerLabel: 'Apply here', submitLabel: 'Sign In as Organizer' },
    'track-manager': { icon: <MapPin size={18} className="text-primary" />, title: 'Track Manager Sign In', registerLink: '/track-register', registerText: 'New track?', registerLabel: 'Register your track', submitLabel: 'Sign In as Track Manager' },
  };

  const cfg = modeConfig[mode as keyof typeof modeConfig];

  return (
    <div className="min-h-screen flex bg-background">
      <BrandPanel />
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={120} />
          </div>
          <button onClick={() => setMode('choose')} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6 transition-colors">
            ← Back
          </button>
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-1">
              {cfg.icon}
              <h2 className="text-2xl font-extrabold uppercase tracking-tight text-foreground">{cfg.title}</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              {cfg.registerText}{' '}
              <Link href={cfg.registerLink} className="text-primary font-semibold hover:text-accent transition-colors">
                {cfg.registerLabel}
              </Link>
            </p>
          </div>
          {authError &&
            <div className="mb-4 px-4 py-3 rounded-lg bg-negative/10 border border-negative/30 text-negative text-sm font-semibold">
              {authError}
            </div>
          }
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Email Address</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="your@email.com" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Your password" className={`${inputCls} pr-10`} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-end">
              <Link href="/forgot-password" className="text-xs text-primary font-semibold hover:text-accent transition-colors">Forgot password?</Link>
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-primary disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {isSubmitting ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Signing In...</> : cfg.submitLabel}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}